import { useRef } from "react";
import type { SignupSourcesResponse } from "@atsready/shared";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { classifyRequestFailure, describeFailure } from "@/lib/blog/request-failure";
import {
  SIGNUP_SOURCE_BAR_CLASS,
  SIGNUP_SOURCE_LABELS,
  UNKNOWN_SOURCE_EXPLANATION,
  describeUnknownSourcesHint,
  signupSourceBadge,
} from "@/lib/signup-source";
import { cn } from "@/lib/utils";

const ENDPOINT = "GET /v1/admin/analytics/signup-sources";
const SKELETON_BAR_WIDTHS = ["88%", "62%", "40%", "30%", "20%", "14%", "10%"];

interface SignupSourcesCardProps {
  days: number;
  data: SignupSourcesResponse | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}

/**
 * Signups by source (M9–M13). Presentational: the page owns the query, and this
 * card renders whichever of error, loading, empty or populated it is handed.
 * Bars keep the api's order, which already sorts by count and keeps LinkedIn
 * and X even at zero.
 */
export function SignupSourcesCard({
  days,
  data,
  isLoading,
  error,
  onRetry,
}: SignupSourcesCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const showLoading = !error && (isLoading || !data);

  const renderBody = () => {
    if (error) {
      const failure = classifyRequestFailure(error);
      return (
        <div className="rounded-md border p-8" data-testid="signup-sources-error">
          <h4 className="text-lg font-semibold">
            {failure.kind === "forbidden"
              ? "Your role cannot view signup sources"
              : "Could not load signup sources"}
          </h4>
          {failure.kind === "failed" && (
            <>
              <p className="mt-2 max-w-prose text-sm text-muted-foreground">
                {describeFailure(failure, ENDPOINT)}
              </p>
              <p className="mt-2 max-w-prose text-sm text-muted-foreground">{failure.message}</p>
              <Button
                className="mt-4"
                onClick={() => {
                  onRetry();
                  // The button unmounts once the retry starts loading, so focus
                  // goes to the card rather than being dropped on the body.
                  cardRef.current?.focus();
                }}
              >
                Retry request
              </Button>
            </>
          )}
        </div>
      );
    }

    if (isLoading || !data) {
      return (
        <div className="flex h-[300px] flex-col gap-4" data-testid="signup-sources-loading">
          <span className="sr-only">Loading signup sources</span>
          <Skeleton className="h-6 w-[45%]" aria-hidden="true" />
          {SKELETON_BAR_WIDTHS.map((width) => (
            <Skeleton key={width} className="h-3" style={{ width }} aria-hidden="true" />
          ))}
        </div>
      );
    }

    if (data.total === 0) {
      return (
        <div className="flex min-h-[300px] items-center justify-center text-center text-sm text-muted-foreground">
          No signups in this period.
        </div>
      );
    }

    const recorded = data.bySource.filter((row) => row.count > 0);
    const allUnknown = recorded.length > 0 && recorded.every((row) => row.source === "unknown");
    const hasUnknown = recorded.some((row) => row.source === "unknown");
    const max = Math.max(0, ...data.bySource.map((row) => row.count));

    return (
      <div className="flex min-h-[300px] flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          <strong className="mr-1 text-2xl font-bold text-foreground">{data.total}</strong> signups
          in the last {days} days
        </p>
        {allUnknown && (
          <p className="rounded-xl bg-info-subtle px-3 py-2 text-sm text-foreground">
            {describeUnknownSourcesHint(data.recordedSince)}
          </p>
        )}
        <ul className="flex flex-col gap-2.5" aria-label={`Signups by source, last ${days} days`}>
          {data.bySource.map((row) => {
            const label = SIGNUP_SOURCE_LABELS[row.source];
            const pct = Math.round((row.count / data.total) * 100);
            const title =
              `${label}: ${row.count} of ${data.total} signups (${pct}%), last ${days} days` +
              (row.source === "unknown" ? ` ${UNKNOWN_SOURCE_EXPLANATION}` : "");
            return (
              <li
                key={row.source}
                title={title}
                className={cn(
                  "grid grid-cols-[76px_minmax(0,1fr)_auto] items-center gap-2.5 text-sm",
                  row.count === 0 && "text-muted-foreground",
                )}
              >
                <span className="whitespace-nowrap font-medium">{label}</span>
                <span className="h-3 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                  <span
                    className={cn("block h-full rounded-full", SIGNUP_SOURCE_BAR_CLASS[row.source])}
                    style={{ width: `${max ? (row.count / max) * 100 : 0}%` }}
                  />
                </span>
                <span className="min-w-[72px] whitespace-nowrap text-right tabular-nums">
                  <span className="font-semibold">{row.count}</span>
                  <span className="ml-1.5 text-muted-foreground">{pct}%</span>
                </span>
              </li>
            );
          })}
        </ul>
        {hasUnknown && (
          <p className="text-sm text-muted-foreground">{UNKNOWN_SOURCE_EXPLANATION}</p>
        )}
        <h4 className="border-t pt-3 text-sm font-semibold">Top campaigns</h4>
        {data.topCampaigns.length > 0 ? (
          <ol className="flex flex-col gap-2 text-sm">
            {data.topCampaigns.map((campaign) => {
              const pill = signupSourceBadge(campaign.source);
              return (
                <li
                  key={`${campaign.campaign}:${campaign.source}`}
                  className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2.5"
                >
                  <span className="truncate font-mono" title={campaign.campaign}>
                    {campaign.campaign}
                  </span>
                  <Badge variant={pill.variant} className={pill.className}>
                    {pill.label}
                  </Badge>
                  <span className="min-w-7 text-right font-semibold tabular-nums">
                    {campaign.count}
                  </span>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">No tagged campaigns in this period.</p>
        )}
      </div>
    );
  };

  return (
    <Card
      ref={cardRef}
      tabIndex={-1}
      role="region"
      aria-labelledby="signup-sources-title"
      aria-busy={showLoading}
    >
      <CardHeader>
        <CardTitle id="signup-sources-title">Signups by source</CardTitle>
        <CardDescription>
          {error ? "Signup sources unavailable" : "Where new accounts came from"}
        </CardDescription>
      </CardHeader>
      <CardContent aria-live="polite">{renderBody()}</CardContent>
    </Card>
  );
}
